import {
  getCreatorPackageAccountConflicts,
  type CreatorCollaborationPackage
} from '@/biz/multiPlatformCollaboration/model';
import { formatSingleBraceTemplate } from '@/biz/multiPlatformCollaboration/presentation';
import { getPlatformName } from '@/biz/platform';
import CreatorPackageRatePanel from '@/components/multiPlatformCollaboration/CreatorPackageRatePanel';
import type { CreatorPackageRateValidation } from '@/biz/multiPlatformCollaboration/packageRates';
import type { CreatorMoneyRange } from '@/interface/creatorOrderV2';
import { Alert } from 'aha-components';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type CreatorPackageRateSectionProps = {
  collaborationPackage: CreatorCollaborationPackage;
  mobile?: boolean;
  quoteTermsReadOnly?: boolean;
  hidePackageRatePanel?: boolean;
  placementTotal: number;
  packageRate: number;
  recommendationRange?: CreatorMoneyRange | null;
  packageRateValidation?: CreatorPackageRateValidation;
  nonNegotiable: boolean;
  message: string;
  includedPlacementIds: string[];
  showConflictValidation: boolean;
  messageLabel?: ReactNode;
  onPackageRateChange: (rate: number) => void;
  onNonNegotiableChange: (value: boolean) => void;
  onMessageChange: (message: string) => void;
};

export default function CreatorPackageRateSection({
  collaborationPackage,
  mobile = false,
  quoteTermsReadOnly = false,
  hidePackageRatePanel = false,
  placementTotal,
  packageRate,
  recommendationRange,
  packageRateValidation,
  nonNegotiable,
  message,
  includedPlacementIds,
  showConflictValidation,
  messageLabel,
  onPackageRateChange,
  onNonNegotiableChange,
  onMessageChange
}: CreatorPackageRateSectionProps) {
  const { t } = useTranslation();
  const nonNegotiableLabelId = useId();
  const includedSet = new Set(includedPlacementIds);
  const conflicts = getCreatorPackageAccountConflicts(
    collaborationPackage
  ).filter(({ placement }) => includedSet.has(placement.id));

  return (
    <>
      {!hidePackageRatePanel ? (
        <CreatorPackageRatePanel
          mobile={mobile}
          quoteTermsReadOnly={quoteTermsReadOnly}
          placementTotal={placementTotal}
          packageRate={packageRate}
          recommendationRange={recommendationRange}
          packageRateValidation={packageRateValidation}
          onPackageRateChange={onPackageRateChange}
        />
      ) : null}

      {showConflictValidation
        ? conflicts.map(({ placement, ownerEmail }) => (
            <p
              key={placement.id}
              className="mb-0 mt-2 text-xs leading-[18px] text-[#DC2626]"
              role="alert"
            >
              {formatSingleBraceTemplate(
                ownerEmail
                  ? t('multi_platform.quote.conflict_summary', {
                      defaultValue:
                        'This {platform} channel {channels} is connected to another account ({email}). To submit your quote, remove it from your submission, or disconnect it from that account and reconnect it here.'
                    })
                  : t('multi_platform.quote.conflict_summary_no_email', {
                      defaultValue:
                        'This {platform} channel {channels} is connected to another account. To submit your quote, remove it from your submission, or disconnect it from that account and reconnect it here.'
                    }),
                {
                  platform: getPlatformName(placement.platform),
                  channels: `@${(placement.userName || 'creator').replace(/^@+/, '')}`,
                  email: ownerEmail || ''
                }
              )}
            </p>
          ))
        : null}

      <section className={mobile ? 'mt-4' : 'mt-3'}>
        <div
          className={`rounded-2xl bg-gradient-to-b from-[#F0F1F3] ${
            mobile ? 'min-h-[93px] to-white p-2' : 'to-transparent p-3'
          }`}
        >
          <div
            className={`flex flex-col gap-3 rounded-lg border-2 border-[#E5E7EB] bg-white ${
              mobile
                ? 'min-h-[76px] px-2 py-2 shadow-[0_15px_46px_rgba(14,24,39,0.08)]'
                : 'px-4 py-3'
            }`}
          >
            <div
              className={`flex w-full items-center justify-between ${mobile ? 'min-h-[56px] gap-0' : 'gap-4'}`}
            >
              <div className="min-w-0 flex-1">
                <h3
                  id={nonNegotiableLabelId}
                  className={`m-0 font-semibold text-[#292524] ${
                    mobile
                      ? 'text-sm leading-5'
                      : 'text-base leading-6 tracking-[-0.16px]'
                  }`}
                >
                  {mobile
                    ? t('multi_platform.quote.fixed_non_negotiable', {
                        defaultValue: 'Make this rate fixed (non-negotiable)'
                      })
                    : t('multi_platform.quote.non_negotiable', {
                        defaultValue: 'Make this rate non-negotiable'
                      })}
                </h3>
                <p
                  className={`m-0 text-[#57534D] ${
                    mobile
                      ? 'mt-1 text-xs leading-[18px]'
                      : 'text-sm leading-[22px] tracking-[-0.14px]'
                  }`}
                >
                  {mobile
                    ? t(
                        'multi_platform.quote.fixed_non_negotiable_description',
                        {
                          defaultValue:
                            'After opening, the brand can only accept or reject.'
                        }
                      )
                    : t('multi_platform.quote.non_negotiable_description', {
                        defaultValue:
                          'Once turned on, the brand can only accept or decline.'
                      })}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-labelledby={nonNegotiableLabelId}
                aria-checked={nonNegotiable}
                disabled={quoteTermsReadOnly}
                className={`relative flex-shrink-0 rounded-full border-0 p-0 transition-colors ${mobile ? 'h-[23px] w-[42px]' : 'h-[22px] w-[44px]'} ${
                  nonNegotiable
                    ? mobile
                      ? 'bg-[#FF5A1F]'
                      : 'bg-[#FF621D]'
                    : 'bg-[#D0D1DD]'
                }`}
                onClick={() => {
                  if (!quoteTermsReadOnly)
                    onNonNegotiableChange(!nonNegotiable);
                }}
              >
                <span
                  data-testid="non-negotiable-switch-thumb"
                  aria-hidden="true"
                  className={`absolute left-0 rounded-full bg-white shadow transition-transform ${
                    mobile
                      ? 'top-[3px] h-[17px] w-[17px]'
                      : `top-0 h-[22px] w-[22px] border-[1.375px] border-solid ${nonNegotiable ? 'border-[#FF621D]' : 'border-[#D0D1DD]'}`
                  } ${
                    nonNegotiable
                      ? 'translate-x-[22px]'
                      : mobile
                        ? 'translate-x-[3px]'
                        : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {nonNegotiable ? (
              <div
                className="w-full border-t border-[#E7E5E4] pt-3"
                role="alert"
              >
                <Alert
                  type="warning"
                  className="!rounded-lg !py-2 [&>svg]:mt-[3px] [&>div>div]:!leading-[22px] [&>div>div]:tracking-[-0.14px] [&>div>div]:!text-[#292524]"
                  description={t(
                    'multi_platform.quote.non_negotiable_warning',
                    {
                      defaultValue:
                        'This leaves no room to adjust the rate. If it doesn’t fit the brand’s budget, you may be less likely to secure the collaboration.'
                    }
                  )}
                />
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="mt-4">
        <label
          htmlFor="creator-package-message"
          className="text-base font-semibold leading-6 text-[#292524]"
        >
          {messageLabel ??
            t('multi_platform.quote.message_to_brand', {
              defaultValue: 'Message to brand (optional)'
            })}
        </label>
        <textarea
          id="creator-package-message"
          value={message}
          placeholder={t('multi_platform.quote.message_placeholder', {
            defaultValue:
              'Share why this rate makes sense for the collaboration'
          })}
          className="mt-3 block h-[116px] w-full resize-none rounded-xl border border-[#D9D9D9] bg-white px-[11px] py-[7px] text-base leading-[26px] text-[#292524] outline-none placeholder:text-[#C7C2BE] focus:border-[#FF8A65]"
          onChange={(event) => onMessageChange(event.target.value)}
        />
      </section>
    </>
  );
}
